/** Pure helpers for the Projects experience (WP-09 preview): timeline geometry over the visible projects. */
import type { Status } from "./format";
import { STATUS_ORDER } from "./format";

export type ProjectLike = {
  name: string;
  expected_start_date: string;
  expected_end_date: string;
  percent_complete: number | null;
  schedule_status: Status;
  schedule_expected: number | null;
  estimated_costing: number | null;
};

const DAY = 86400000;

export function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(`${s.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Overall date range of the projects (UTC midnight), padded by a few days; null when no project has dates. */
export function timelineRange(projects: ProjectLike[], today: Date = new Date()): { min: Date; max: Date } | null {
  const dates = projects.flatMap((p) => [parseDate(p.expected_start_date), parseDate(p.expected_end_date)]).filter((d): d is Date => !!d);
  if (dates.length === 0) return null;
  const t = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const min = new Date(Math.min(...dates.map((d) => d.getTime()), t.getTime()) - 7 * DAY);
  const max = new Date(Math.max(...dates.map((d) => d.getTime()), t.getTime()) + 7 * DAY);
  return { min, max };
}

/** Left offset and width of a project bar in percent of the range, plus today's position. */
export function barGeometry(p: ProjectLike, range: { min: Date; max: Date }, today: Date = new Date()) {
  const span = range.max.getTime() - range.min.getTime() || 1;
  const pct = (d: Date) => Math.max(0, Math.min(100, ((d.getTime() - range.min.getTime()) / span) * 100));
  const start = parseDate(p.expected_start_date);
  const end = parseDate(p.expected_end_date);
  const t = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  if (!start || !end) return { left: 0, width: 0, today: pct(t), valid: false };
  const left = pct(start);
  const width = Math.max(0.5, pct(end) - left);
  return { left, width, today: pct(t), valid: true };
}

/** Red first, then Amber, Green, Grey; inside a status the largest budget first. */
export function sortByHealth<T extends ProjectLike>(projects: T[]): T[] {
  return [...projects].sort((a, b) => {
    const s = STATUS_ORDER[a.schedule_status] - STATUS_ORDER[b.schedule_status];
    if (s !== 0) return s;
    return (b.estimated_costing ?? 0) - (a.estimated_costing ?? 0);
  });
}

export function countByHealth(projects: ProjectLike[]): Record<Status, number> {
  const out: Record<Status, number> = { Green: 0, Amber: 0, Red: 0, Grey: 0 };
  for (const p of projects) out[p.schedule_status] += 1;
  return out;
}

export function totalBudget(projects: ProjectLike[]): number {
  return projects.reduce((s, p) => s + (p.estimated_costing ?? 0), 0);
}

/** Month tick marks for the timeline header: first day of each month inside the range. */
export function monthTicks(range: { min: Date; max: Date }): { date: Date; left: number }[] {
  const ticks: { date: Date; left: number }[] = [];
  const span = range.max.getTime() - range.min.getTime() || 1;
  const d = new Date(Date.UTC(range.min.getUTCFullYear(), range.min.getUTCMonth() + 1, 1));
  while (d.getTime() <= range.max.getTime()) {
    ticks.push({ date: new Date(d.getTime()), left: ((d.getTime() - range.min.getTime()) / span) * 100 });
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return ticks;
}
